import React, { createContext, useContext, useState } from 'react';
import { alertMessages } from '@/hooks/alertMessages';
import MemCalcImp from '@/interfaces/MemCalcImp';
import { IndexResponseImp } from '@/interfaces/serviceResponses/IndexResponseImp';
import AccountServices from '@/services/AccountServices';
import { useLoading } from './loading';
import { mapIndexList } from '@/lib/utils';
import IndexFlags from '@/enums/IndexFlags';
import SelectOptionImp from '@/interfaces/SelectOptionImp';
import { AWSS3 } from '@/services/S3';
import { useAuth } from './auth';
import LawImp from '@/interfaces/Law';
import InterestIndexesImp from '@/interfaces/calculations/InterestIndexesImp';
import { AllMemcalcsImp } from '@/services/CalculationsServices/CurrentAccountService/AccountService';
import http from '@/services/http';

interface FactorsHookImp {
  getIndexes: () => IndexResponseImp[];
  loadIndexes: Function;
  indexesOptions: SelectOptionImp[];
  allMemcalcs: {
    [key: number]: MemCalcImp[];
  };
  memcalcs: MemCalcImp[];
  setMemcalcs: React.Dispatch<React.SetStateAction<MemCalcImp[]>>;
  interestIndexesFromLaw: LawImp[];
  setInterestIndexesFromLaw: React.Dispatch<React.SetStateAction<LawImp[]>>;
  interestIndexes: InterestIndexesImp;
  setInterestIndexes: React.Dispatch<React.SetStateAction<InterestIndexesImp>>;
  isMemcalcLoading: boolean;
}

const FactorsHookContext = createContext<FactorsHookImp | null>(null);

export const FactorsHookProvider = ({ children }: { children: React.ReactElement }) => {
  const { closeLoading, openLoading } = useLoading();

  const alertMessage = alertMessages();
  const accountServices = new AccountServices();
  const [allMemcalcs, setAllMemcalcs] = useState<AllMemcalcsImp>([]);
  const [memcalcs, setMemcalcs] = useState<MemCalcImp[]>([]);
  const [interestIndexesFromLaw, setInterestIndexesFromLaw] = useState<LawImp[]>([]);
  const [isMemcalcLoading, setIsMemcalcLoading] = useState(false);

  const [interestIndexes, setInterestIndexes] = useState<InterestIndexesImp>({
    newSavings: [],
    newSavingsDaily: [],
    savings: [],
    selic: [],
    tr: [],
  });

  const { isAuth } = useAuth();

  const [indexes, setIndexes] = useState<IndexResponseImp[]>([]);
  const [indexesOptions, setIndexesOptions] = useState<SelectOptionImp[]>([]);

  const getIndexes = () => {
    return indexes;
  };

  const onloadIndex = (indexes: IndexResponseImp[]) => {
    console.info('on_change_index');

    const withoutCorrectionIndexOption = {
      id: '-1',
      label: 'Sem correção',
      value: '-1',
    };

    const mappedIndexList = mapIndexList(indexes, IndexFlags.CORRECAO_PELO_SISTEMA);
    const selectList: SelectOptionImp[] = mappedIndexList.map(calcIndex => {
      const calcIndexOpton: SelectOptionImp = {
        id: Number(calcIndex.id),
        label: calcIndex.name,
        value: Number(calcIndex.id),
        disp: calcIndex.disp,
      };
      return calcIndexOpton;
    });

    selectList.unshift(withoutCorrectionIndexOption);
    setIndexesOptions(selectList);
  };

  const loadIndexes = async () => {
    try {
      openLoading();
      const indexesResponse: IndexResponseImp[] = await accountServices.listIndexes();

      setIndexes(indexesResponse);
      onloadIndex(indexesResponse);
    } catch (error) {
      closeLoading();
      alertMessage.error('Erro ao carregar o índice');
    } finally {
      closeLoading();
    }
  };

  const getAllMemcalcs = async () => {
    let key = '';
    try {
      setIsMemcalcLoading(true);
      key = `memcalcs.json`;

      const url = await new AWSS3().getValue(key);
      const res = await fetch(url);
      const jsonData = await res.json();

      setAllMemcalcs(jsonData);
      setIsMemcalcLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    if (!isAuth) return;
    getAllMemcalcs();
  }, [isAuth]);

  return (
    <FactorsHookContext.Provider
      value={{
        interestIndexes,
        setInterestIndexes,
        interestIndexesFromLaw,
        setInterestIndexesFromLaw,
        memcalcs,
        setMemcalcs,
        indexesOptions,
        isMemcalcLoading,
        getIndexes,
        loadIndexes,
        allMemcalcs,
      }}>
      {children}
    </FactorsHookContext.Provider>
  );
};

export const useFactors = (): FactorsHookImp => {
  const socket = useContext(FactorsHookContext);

  if (!socket) {
    throw new Error('useFactors deve ser usado dentro de um FactorsHookProvider');
  }

  return socket;
};
